import { styled, Box, IconButton, Popover, PopoverOrigin } from '@material-ui/core';
import React, { FC, MouseEvent, ReactNode, useState, useMemo, useCallback } from 'react';
import { InfoOutlined } from '@material-ui/icons';
import { MetricTooltip } from '@/components/pages/blockSchedule/metrics/MetricTooltip';

type MetricHeaderProps = { title: string; tooltip?: string; children?: ReactNode };

const ANCHOR_ORIGIN: PopoverOrigin = {
  vertical: 'bottom',
  horizontal: 'center',
};

const TRANSFORM_ORIGIN: PopoverOrigin = {
  vertical: 'top',
  horizontal: 'center',
};

export const MetricHeader: FC<MetricHeaderProps> = ({ title, tooltip, children }) => {
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);

  const handleClick = useCallback((event: MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  }, []);

  const handleClose = useCallback(() => {
    setAnchorEl(null);
  }, []);

  const id = useMemo(() => (anchorEl ? 'metric-tooltip' : undefined), [anchorEl]);

  return (
    <StyledMetricHeader>
      {title}
      {!!tooltip && (
        <>
          <IconButton size="small" aria-describedby={id} onClick={handleClick}>
            <InfoOutlined fontSize="small" color="primary" />
          </IconButton>
          <Popover
            id={id}
            open={!!anchorEl}
            anchorEl={anchorEl}
            onClose={handleClose}
            anchorOrigin={ANCHOR_ORIGIN}
            transformOrigin={TRANSFORM_ORIGIN}
          >
            <MetricTooltip title={title} description={tooltip} />
          </Popover>
        </>
      )}
      {children}
    </StyledMetricHeader>
  );
};

const StyledMetricHeader = styled(Box)(({ theme }) => ({
  fontSize: '0.875rem',
  fontWeight: theme.typography.fontWeightMedium as number,
  color: theme.palette.text.secondary,
  marginBottom: theme.spacing(1),
  lineHeight: 1.3,
  textAlign: 'center',
  letterSpacing: '0.025em',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: theme.spacing(1),
  [theme.breakpoints.down('md')]: {
    fontSize: '0.8rem',
    marginBottom: theme.spacing(0.75),
  },
}));
